import { useId, useState } from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import type { SxProps, Theme } from '@mui/material/styles'
import { RiArrowDownSLine } from 'react-icons/ri'

export interface DashboardStatusFilterOption<T extends string> {
  value: T
  label: string
}

interface Props<T extends string> {
  value: T
  onChange: (value: T) => void
  options: readonly DashboardStatusFilterOption<T>[]
  filterWidth: number
  testId: string
  labelPrefix?: string
  /** Gap between label prefix and selected value (px). */
  labelValueGapPx?: number
  /** `prefix-value` (default) shows "Label: Value"; `label-only` shows label text only (Figma SP list). */
  triggerDisplay?: 'prefix-value' | 'label-only'
  chevronSizePx?: number
  /** When true, active border color matches open state (e.g. non-default filter selected). */
  borderActive?: boolean
  wrapperTestId?: string
  wrapperSx?: SxProps<Theme>
}

/** Shared Figma-style status filter dropdown (225×42) for admin + editor/viewer dashboards. */
export function DashboardStatusFilter<T extends string>({
  value,
  onChange,
  options,
  filterWidth,
  testId,
  labelPrefix = 'Filter by Status',
  labelValueGapPx = 4,
  triggerDisplay = 'prefix-value',
  chevronSizePx = 24,
  borderActive = false,
  wrapperTestId,
  wrapperSx,
}: Readonly<Props<T>>) {
  const labelId = useId()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)

  const selectedLabel = options.find((o) => o.value === value)?.label ?? 'All'

  const trigger = (
    <>
      <ButtonBase
        aria-labelledby={labelId}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={testId}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          width: { xs: '100%', sm: filterWidth },
          minWidth: { xs: '100%', sm: filterWidth },
          height: 42,
          flexShrink: 0,
          borderRadius: '10px',
          border: '1px solid',
          borderColor: open || borderActive ? '#008cd0' : '#e1e7f3',
          bgcolor: '#ffffff',
          px: '16px',
          py: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          textAlign: 'left',
          '&:hover': { bgcolor: '#ffffff' },
          '&.Mui-focusVisible': {
            borderColor: '#008cd0',
            outline: 'none',
          },
        }}
      >
        <Typography
          id={labelId}
          component="span"
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            fontSize: 14,
            lineHeight: triggerDisplay === 'label-only' ? 'normal' : '20px',
            letterSpacing: '-0.1504px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: triggerDisplay === 'label-only' ? 0 : `${labelValueGapPx}px`,
            overflow: 'hidden',
          }}
        >
          {triggerDisplay === 'label-only' ? (
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                color: '#64748b',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {labelPrefix}
            </Box>
          ) : (
            <>
              <Box component="span" sx={{ fontWeight: 600, color: '#64748b', flexShrink: 0 }}>
                {labelPrefix}:
              </Box>
              <Box
                component="span"
                sx={{
                  color: '#04183f',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {selectedLabel}
              </Box>
            </>
          )}
        </Typography>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: chevronSizePx,
            height: chevronSizePx,
            flexShrink: 0,
            color: '#64748b',
          }}
          aria-hidden
        >
          <RiArrowDownSLine size={chevronSizePx} />
        </Box>
      </ButtonBase>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: '4px',
              minWidth: anchorEl?.offsetWidth ?? filterWidth,
              borderRadius: '10px',
              boxShadow: '0px 4px 12px rgba(13, 27, 42, 0.12)',
            },
          },
        }}
        MenuListProps={{ role: 'listbox', 'aria-labelledby': labelId }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === value}
            onClick={() => {
              onChange(option.value)
              setAnchorEl(null)
            }}
            sx={{
              fontSize: 14,
              lineHeight: '20px',
              letterSpacing: '-0.1504px',
              color: '#04183f',
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  )

  if (wrapperTestId) {
    return (
      <Box data-testid={wrapperTestId} sx={{ alignSelf: 'flex-end', ...wrapperSx }}>
        {trigger}
      </Box>
    )
  }

  return trigger
}
